import { cn } from '@/lib/cn'

/**
 * Line-icon set for the content cards.
 *
 * All icons share one 24×24 grid, a 1.6 stroke and `currentColor`, so they sit
 * consistently at any size and inherit the surrounding text colour. They are
 * decorative — every card states its subject in text — so the wrapper is
 * hidden from assistive technology.
 */
export type IconName =
  // Sections
  | 'overview'
  | 'finance'
  | 'sales'
  | 'marketing'
  | 'operations'
  | 'supply'
  | 'compliance'
  // Pipeline
  | 'land'
  | 'parse'
  | 'discover'
  | 'profile'
  | 'calendar'
  | 'normalize'
  | 'reconcile'
  | 'assemble'
  // Forecast ladder
  | 'fixture'
  | 'model'
  | 'model-plus'
  | 'classical'
  | 'bootstrap'
  | 'locked'
  // Trace
  | 'source'
  | 'extraction'
  | 'calculation'
  | 'check'
  | 'projection'
  // Misc
  | 'recorded'
  | 'header-row'
  | 'brackets'
  | 'grouping'
  | 'sigma'
  | 'scale-note'
  | 'send'
  | 'terminal'

const PATHS: Record<IconName, JSX.Element> = {
  overview: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="4.5" rx="1.5" />
      <rect x="13.5" y="10.5" width="7.5" height="10.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  finance: (
    <>
      <path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Z" />
      <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
    </>
  ),
  sales: (
    <>
      <path d="M3 4h18l-7 8v7l-4 2v-9L3 4Z" />
    </>
  ),
  marketing: (
    <>
      <path d="M3 16v-4a2 2 0 0 1 2-2h3l7-4v14l-7-4H5a2 2 0 0 1-2-2Z" />
      <path d="M18 9a4 4 0 0 1 0 6" />
    </>
  ),
  operations: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v3M12 19v3M22 12h-3M5 12H2M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1M18.4 18.4l-2.1-2.1M7.7 7.7 5.6 5.6" />
    </>
  ),
  supply: (
    <>
      <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </>
  ),
  compliance: (
    <>
      <path d="M12 3l7.5 3v6c0 4.5-3 8.2-7.5 9.5C7.5 20.2 4.5 16.5 4.5 12V6L12 3Z" />
      <path d="m9 12 2.2 2.2L15.5 10" />
    </>
  ),
  land: (
    <>
      <path d="M12 3v10M8 9.5l4 4 4-4" />
      <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </>
  ),
  parse: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5M8.5 13h7M8.5 16.5h4" />
    </>
  ),
  discover: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
      <circle cx="15.5" cy="14.5" r="2.6" />
      <path d="m17.6 16.6 1.9 1.9" />
    </>
  ),
  profile: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16M15 4v16" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
      <path d="M11 14h2v2h-2z" fill="currentColor" stroke="none" />
    </>
  ),
  normalize: (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" />
      <circle cx="9" cy="7" r="2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="17" r="2" fill="currentColor" stroke="none" />
    </>
  ),
  reconcile: (
    <>
      <path d="M12 4v16M6 8H3l3-4 3 4H6ZM18 8h-3l3-4 3 4h-3Z" />
      <path d="M3 8c0 2.2 1.3 4 3 4s3-1.8 3-4M15 8c0 2.2 1.3 4 3 4s3-1.8 3-4" />
      <path d="M8 20h8" />
    </>
  ),
  assemble: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <path d="M13 17h8M17 13v8" />
    </>
  ),
  fixture: (
    <>
      <path d="M9 3v6.5L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 9.5V3" />
      <path d="M8 3h8M7.5 14h9" />
    </>
  ),
  model: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h3l2-4 3 8 2.5-5 1.5 3h5" />
    </>
  ),
  'model-plus': (
    <>
      <circle cx="10.5" cy="12" r="7.5" />
      <path d="M3 12h2.5l2-3.5 2.5 7 2-4.5 1.5 2.5H18" />
      <path d="M19.5 6v5M17 8.5h5" />
    </>
  ),
  classical: (
    <>
      <path d="M4 4v16h16" />
      <path d="m7 15 3.5-4.5 3 2.5L19 7" />
    </>
  ),
  bootstrap: (
    <>
      <path d="M4 8h12a4 4 0 0 1 0 8H8" />
      <path d="m7 5 3 3-3 3M11 13l-3 3 3 3" />
    </>
  ),
  locked: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  source: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5" />
      <circle cx="12" cy="14.5" r="2.2" />
    </>
  ),
  extraction: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11" />
      <rect x="11" y="11" width="6" height="4" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  calculation: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12.5h3M8 16.5h3M14.5 12.5h2M14.5 16.5h2" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.2 2.4 2.4 4.6-5" />
    </>
  ),
  projection: (
    <>
      <path d="M3 18h18" />
      <path d="M4 14c3-6 6 2 8-2s5-4 8-6" />
      <path d="M4 17.5c3-4 6 1 8-1.5s5-2 8-3.5" strokeDasharray="2.5 2.5" />
    </>
  ),
  recorded: (
    <>
      <path d="M5 3h11l4 4v14H5V3Z" />
      <path d="M16 3v4h4" />
      <path d="m8.5 14 2.2 2.2 4.6-4.8" />
    </>
  ),
  'header-row': (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <rect x="3" y="4" width="18" height="4.5" rx="2" fill="currentColor" stroke="none" />
      <path d="M3 13h18M9 8.5V20" />
    </>
  ),
  // A terminal: the chrome of a window, a prompt caret and a cursor rule. Same 24x24 grid and
  // 1.6 stroke as the rest of the set, so it sits correctly beside the button label.
  terminal: (
    <>
      <rect x="2.5" y="4" width="19" height="16" rx="2.5" />
      <path d="M2.5 8.5h19" />
      <path d="M7 12.5l2.5 2.25L7 17" />
      <path d="M12.5 17h4.5" />
    </>
  ),
  brackets: (
    <>
      <path d="M9 4C6.5 6 5.5 8.8 5.5 12s1 6 3.5 8M15 4c2.5 2 3.5 4.8 3.5 8s-1 6-3.5 8" />
      <path d="M10.5 12h3" />
    </>
  ),
  grouping: (
    <>
      <path d="M4 6h4M10 6h10M4 12h10M16 12h4M4 18h6M12 18h8" />
    </>
  ),
  sigma: (
    <>
      <path d="M17 4H7l6 8-6 8h10" />
    </>
  ),
  'scale-note': (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M6.5 8.5h7" />
      <path d="M6.5 13h11M6.5 16.5h8" />
    </>
  ),
  send: (
    <>
      <path d="m21 3-9.5 9.5M21 3l-6.5 18-4-8-8-4L21 3Z" />
    </>
  ),
}

export function Icon({
  name,
  className,
  strokeWidth = 1.6,
}: {
  name: IconName
  className?: string
  strokeWidth?: number
}) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('h-5 w-5', className)}
    >
      {PATHS[name]}
    </svg>
  )
}

/**
 * The rounded tile the card icons sit in. Tints and lifts with its card on
 * hover, which is what makes a grid of cards feel alive rather than flat.
 */
export function IconBadge({
  name,
  tone = 'primary',
  className,
}: {
  name: IconName
  tone?: 'primary' | 'violet' | 'projected' | 'computes' | 'ink'
  className?: string
}) {
  const tones = {
    primary: 'border-primary/20 bg-primary/10 text-primary',
    violet: 'border-violet/20 bg-violet/10 text-violet',
    projected: 'border-projected/25 bg-projected/10 text-[#9a6212]',
    computes: 'border-computes/25 bg-computes/10 text-computes',
    ink: 'border-white/20 bg-white/10 text-white',
  } as const

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-11 w-11 flex-none items-center justify-center rounded-card border transition-all duration-300 ease-brand group-hover:scale-105',
        tones[tone],
        className,
      )}
    >
      <Icon name={name} />
    </span>
  )
}
